import React, { useMemo } from "react";

import Button from "components/buttons/Button";
import CheckBoxCard from "components/checkBoxCard/CheckBoxCard";
import RegularSelect from "components/radioButtonCard/RegularSelect";
import RadioButtonCard from "components/radioButtonCard/RadioButtonCard";
import SearchInputWithChipCard from "components/searchInputWithChip/SearchInputWithChipCard";
import Spacer from "components/Spacer/Spacer";

import useModal from "hooks/useModal";
import useToastify from "hooks/useToastify";

import { primary_care_sub_steps } from "helpers/enum";
import HistoryDebounceSearchModal from "../../dermatology/medicalHistory/HistoryDebounceSearchModal";

const TOBACCO_FORMS = [
  "Cigarettes",
  "Vape",
  "Cannabis",
  "Chew",
  "Cigar",
  "Pipe",
];

function normalizeMedicalConditions(selected) {
  if (!Array.isArray(selected)) return [];
  return selected
    .filter(Boolean)
    .map((el) => {
      const category = el?.category ?? "Other";
      const disease = el?.disease ?? "";
      const id = el?.id ?? `${category}:${disease}`;
      return { ...el, category, disease, id };
    })
    .filter((el) => el?.disease?.trim()?.length);
}

function SocialHistory({
  incompleteData,
  updateVisit,
  updatingVisit,
  setActiveIndex,
  currentPatient,
  firstAnswer,
  setFirstAnswer,
  secondAnswer,
  setSecondAnswer,
  thirdAnswer,
  setThirdAnswer,
  thirdYesAnswer,
  setThirdYesAnswer,
  fourthAnswer,
  setFourthAnswer,
  fourthYesAnswer,
  setFourthYesAnswer,
}) {
  const { setShowModal } = useModal();
  const { showAlert } = useToastify();

  const isValid = useMemo(() => {
    const firstOk = firstAnswer?.length > 0;
    const secondOk = secondAnswer?.length > 0;
    const thirdOk = thirdAnswer?.length > 0;

    const tobaccoOk =
      thirdAnswer === "No"
        ? true
        : thirdAnswer === "Yes"
        ? thirdYesAnswer?.length > 0
        : false;

    const medicalOk =
      fourthAnswer === "None"
        ? true
        : fourthAnswer?.length
        ? fourthYesAnswer?.length > 0
        : false;

    return firstOk && secondOk && thirdOk && tobaccoOk && medicalOk;
  }, [firstAnswer, secondAnswer, thirdAnswer, thirdYesAnswer, fourthAnswer, fourthYesAnswer]);


  function handleOnSelectThirdYesAnswer(el) {
    let final = [...thirdYesAnswer];
    if (final?.includes(el)) {
      setThirdYesAnswer(final.filter((elem) => elem !== el));
    } else {
      setThirdYesAnswer([...final, el]);
    }
  }

  function buildSelectedOptions() {
    const refined = [];
    normalizeMedicalConditions(fourthYesAnswer).forEach((el) => {
      const existingIndex = refined?.findIndex(
        (answer) => answer.category === el.category
      );
      if (existingIndex > -1) {
        refined[existingIndex].diseases.push(el?.disease);
      } else {
        refined.push({
          category: el.category,
          diseases: [el?.disease],
        });
      }
    });
    return refined;
  }

  function onRightButtonClick() {
    const selected_options = fourthAnswer === "None" ? [] : buildSelectedOptions();

    const payloadValue = [
      {
        health_profile_section: "exercise",
        question: "Do you exercise regularly?",
        answer_type_boolean: true,
        answer: firstAnswer.toUpperCase(),
      },
      {
        health_profile_section: "alcohol",
        question: "Do you drink alcohol?",
        answer_type_boolean: false,
        answer: secondAnswer,
      },
      {
        health_profile_section: "tobacco",
        question: "Do you smoke?",
        answer_type_boolean: true,
        answer: thirdAnswer.toUpperCase(),
        form_of_tobacco: thirdAnswer === "Yes" ? thirdYesAnswer : [],
      },
      {
        health_profile_section: "medical_history",
        question: "Current medical conditions under treatment",
        answer_type_boolean: true,
        answer: fourthAnswer === "None" ? "NO" : "YES",
        selected_options,
      },
    ];

    updateVisit({
      payload: {
        case: {
          id: incompleteData?.id,
          social_or_medical_history: payloadValue,
          allergies: incompleteData?.allergies_json,
          medications:
            typeof incompleteData?.medications[0] === "string"
              ? incompleteData?.medications.map((item) => ({
                  name: item,
                }))
              : incompleteData?.medications?.map((med) => {
                  return {
                    ...med?.details,
                    name: med?.name,
                  };
                }),
          pc_second_phase_steps: {
            ...incompleteData?.pc_second_phase_steps,
            [primary_care_sub_steps.social_history]: "completed",
          },
        },
      },
      change_current_screen: false,
      screen_to_complete: null,
      callBack: () => {
        showAlert("Progress Saved Successfully", "success");
        setActiveIndex(8);
      },
    });
  }

  return (
    <div className="bg-white shadow-card rounded-b-4xl px-10 pb-10 relative overflow-x-hidden">
      <RegularSelect
        label="Do you exercise regularly?"
        options={["Yes", "No"]}
        selected={firstAnswer}
        setSelected={setFirstAnswer}
        orange={true}
      />

      <Spacer height={20} />

      <RegularSelect
        label="Do you drink alcohol?"
        options={["Never", "Occasionally", "Daily", "Previously/Quit"]}
        selected={secondAnswer}
        setSelected={setSecondAnswer}
        orange={true}
        mobile={true}
      />

      <Spacer height={20} />

      <RegularSelect
        label="Do you smoke?"
        options={["Yes", "No"]}
        selected={thirdAnswer}
        setSelected={(el) => {
          setThirdAnswer(el);
          if (el === "No") setThirdYesAnswer([]);
        }}
        orange={true}
      />

      {thirdAnswer === "Yes" ? (
        <div>
          <Spacer height={20} />
          <CheckBoxCard
            options={TOBACCO_FORMS}
            selected={thirdYesAnswer}
            onSelect={handleOnSelectThirdYesAnswer}
            variant="small"
          />
        </div>
      ) : null}

      <Spacer height={20} />

      <p className="font-medium text-2xl">
        Please add any medical conditions for which you are under treatment:
      </p>

      <Spacer height={20} />

      <SearchInputWithChipCard
        inputLabel={"Search Medical Conditions"}
        onClickInput={() => {
          if (updatingVisit) return;
          setFourthAnswer("Yes");
          setShowModal({
            state: true,
            children: (_, onClose) => {
              return (
                <HistoryDebounceSearchModal
                  keyToSearch={"medical-history-options"}
                  title={"Search Medical Conditions"}
                  onClose={onClose}
                  selected={normalizeMedicalConditions(fourthYesAnswer)}
                  setSelected={(next) =>
                    setFourthYesAnswer(normalizeMedicalConditions(next))
                  }
                />
              );
            },
            data: null,
          });
        }}
        selected={normalizeMedicalConditions(fourthYesAnswer)}
        setSelected={(next) =>
          setFourthYesAnswer(normalizeMedicalConditions(next))
        }
      />

      <Spacer height={20} />

      <RadioButtonCard
        options={["None"]}
        selected={fourthAnswer}
        onSelect={() => {
          if (fourthAnswer === "None") {
            setFourthAnswer("Yes");
          } else {
            setFourthAnswer("None");
            setFourthYesAnswer([]);
          }
        }}
      />

      <Button
        className="mt-10 py-4"
        disabled={!isValid}
        loading={updatingVisit}
        label="Next"
        onClick={onRightButtonClick}
      />
    </div>
  );
}

export default SocialHistory;

